import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminApi, useAuth } from "@/context/AuthContext";
import { formatEventDate, formatShortDate, formatTime, isPast, useApi, usePagedList } from "@/lib/api";
import { ResourcePanel } from "@/components/admin/ResourcePanel";
import { EventForm } from "@/components/admin/EventForm";
import { ArticleForm, PodcastForm } from "@/components/admin/ArticleForm";
import { TrackForm } from "@/components/admin/TrackForm";
import { ArtistForm } from "@/components/admin/ArtistForm";
import { PlaceForm } from "@/components/admin/PlaceForm";
import { EventAlertButton } from "@/components/admin/EventAlertButton";
import { BulkImport } from "@/components/admin/BulkImport";
import { PlaceReview } from "@/components/admin/PlaceReview";
import { MixcloudAutolink } from "@/components/admin/MixcloudPicker";
import { HoursSync } from "@/components/admin/HoursSync";

const PLACE_LABEL = { disquaires: "Disquaire", salles: "Salle", bars: "Bar / Resto", clubs: "Club" };

const tabCls = "rounded-none font-display uppercase text-sm md:text-base px-4 md:px-6 py-3 text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-black";

const useAdminList = (path) => useQuery({ queryKey: [path], queryFn: async () => (await adminApi.get(path)).data });

const Messages = () => {
  const { data = [] } = useAdminList("/admin/messages");
  if (!data.length) return <p className="text-zinc-400" data-testid="admin-messages-empty">Aucun message pour l'instant.</p>;
  return (
    <ul className="space-y-4" data-testid="admin-messages-list">
      {data.map((m) => (
        <li key={m.id} className="border border-white/10 p-5" data-testid={`admin-message-${m.id}`}>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary">{m.subject} · {formatShortDate(m.created_at)}</p>
          <p className="text-white font-semibold mt-2">{m.name} — <a href={`mailto:${m.email}`} className="underline hover:text-primary">{m.email}</a></p>
          <p className="text-zinc-300 mt-2 whitespace-pre-line">{m.message}</p>
        </li>
      ))}
    </ul>
  );
};

const Newsletter = () => {
  const { data = [] } = useAdminList("/admin/newsletter");
  const csv = "data:text/csv;charset=utf-8," + encodeURIComponent(["email,date,villes", ...data.map((d) => `${d.email},${d.created_at},"${(d.cities || []).join(" / ")}"`)].join("\n"));
  return (
    <div data-testid="admin-newsletter">
      <div className="flex items-center justify-between mb-6">
        <p className="text-zinc-400 text-sm">{data.length} inscrit(s)</p>
        <a href={csv} download="newsletter-lgtdz.csv" data-testid="admin-newsletter-export" className="border border-white/30 text-white uppercase text-xs font-bold tracking-[0.2em] px-5 py-3 hover:border-white transition-colors">Exporter CSV</a>
      </div>
      <ul className="border-t border-white/10">
        {data.map((d) => (
          <li key={d.email} className="flex justify-between gap-4 py-3 border-b border-white/10 text-sm" data-testid={`admin-newsletter-row-${d.email}`}>
            <span className="text-white min-w-0 truncate">{d.email}{d.cities?.length > 0 && <span className="ml-3 text-primary text-xs uppercase tracking-[0.15em]">Alertes : {d.cities.join(", ")}</span>}</span>
            <span className="text-zinc-400 shrink-0">{formatShortDate(d.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Donations = () => {
  const { data } = useAdminList("/admin/donations");
  const items = data?.items || [];
  return (
    <div data-testid="admin-donations">
      <p className="font-display text-4xl text-white mb-2" data-testid="admin-donations-total">{(data?.total || 0).toFixed(2)} €</p>
      <p className="text-zinc-400 text-sm mb-6">{items.length} don(s) reçu(s)</p>
      <ul className="border-t border-white/10">
        {items.map((d) => (
          <li key={d.order_id} className="py-4 border-b border-white/10" data-testid={`admin-donation-${d.order_id}`}>
            <p className="text-white font-semibold">{d.amount.toFixed(2)} € — {d.name || "Anonyme"} <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-primary">{d.provider === "stripe" ? (d.kind === "monthly" ? "Carte · mensuel" : "Carte") : "PayPal"}</span> <span className="text-zinc-500 font-normal text-sm">{d.payer_email}</span></p>
            <p className="text-zinc-500 text-sm">{formatShortDate(d.created_at)}{d.message ? ` · « ${d.message} »` : ""}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const RowTitle = ({ title, sub, tag }) => (
  <>
    <p className="text-white font-semibold truncate">{title}{tag && <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{tag}</span>}</p>
    <p className="text-zinc-400 text-sm truncate">{sub}</p>
  </>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const events = usePagedList("/events", { when: "all" }, 50);
  const news = usePagedList("/news", {}, 50);
  const tracks = useApi("/arrivages");
  const podcasts = usePagedList("/podcasts", {}, 50);
  const artists = usePagedList("/artists", {}, 50);
  const places = useApi("/places");

  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/brand/logo.png" alt="LGTDZ" className="h-10 w-auto" />
            <span className="font-thin-brand uppercase text-lg text-white hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-300 hover:text-white"><ExternalLink className="w-4 h-4" /> Voir le site</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold hover:border-primary hover:text-primary transition-colors"><LogOut className="w-4 h-4" /> Déconnexion</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-5 md:px-10 py-12">
        <h1 className="uppercase text-5xl md:text-6xl leading-none mb-10"><span className="font-display">Salut</span> <span className="font-thin-brand">l'équipe</span></h1>
        <Tabs defaultValue="events">
          <TabsList className="rounded-none bg-transparent border border-white/20 p-0 h-auto flex-wrap mb-10">
            <TabsTrigger value="events" className={tabCls} data-testid="admin-tab-events">Concerts</TabsTrigger>
            <TabsTrigger value="news" className={tabCls} data-testid="admin-tab-news">News</TabsTrigger>
            <TabsTrigger value="arrivages" className={tabCls} data-testid="admin-tab-arrivages">Arrivages</TabsTrigger>
            <TabsTrigger value="podcasts" className={tabCls} data-testid="admin-tab-podcasts">Podcasts</TabsTrigger>
            <TabsTrigger value="artists" className={tabCls} data-testid="admin-tab-artists">Artistes</TabsTrigger>
            <TabsTrigger value="places" className={tabCls} data-testid="admin-tab-places">Lieux</TabsTrigger>
            <TabsTrigger value="messages" className={tabCls} data-testid="admin-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="newsletter" className={tabCls} data-testid="admin-tab-newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="donations" className={tabCls} data-testid="admin-tab-donations">Dons</TabsTrigger>
          </TabsList>
          <TabsContent value="events">
            <BulkImport />
            <ResourcePanel resource="events" queryKey="/events" label="un concert" items={events.items} list={events} detailPath="/events" Form={EventForm}
              rowActions={(e) => !isPast(e.date) && e.city ? <EventAlertButton event={e} /> : null}
              renderRow={(e) => <RowTitle title={e.title} sub={`${formatEventDate(e.date)} · ${formatTime(e.date)}${e.venue ? ` — ${e.venue}` : ""}${e.city ? `, ${e.city}` : ""}`} tag={isPast(e.date) ? "passé" : "à venir"} />} />
          </TabsContent>
          <TabsContent value="news">
            <ResourcePanel resource="news" queryKey="/news" label="une news" items={news.items} list={news} detailPath="/news" Form={ArticleForm}
              renderRow={(n) => <RowTitle title={n.title} sub={formatShortDate(n.date)} />} />
          </TabsContent>
          <TabsContent value="arrivages">
            <ResourcePanel resource="arrivages" queryKey="/arrivages" label="un morceau" items={tracks.data} Form={TrackForm}
              formProps={{ nextRank: (tracks.data?.length || 0) + 1 }}
              renderRow={(t) => <RowTitle title={`${String(t.rank).padStart(2, "0")} · ${t.title}`} sub={t.artist} tag={t.youtube_id ? "youtube" : null} />} />
          </TabsContent>
          <TabsContent value="podcasts">
            <MixcloudAutolink />
            <ResourcePanel resource="podcasts" queryKey="/podcasts" label="un podcast" items={podcasts.items} list={podcasts} detailPath="/podcasts" Form={PodcastForm}
              renderRow={(p) => <RowTitle title={p.title} sub={formatShortDate(p.date)} tag={p.embeds?.length ? "player" : null} />} />
          </TabsContent>
          <TabsContent value="artists">
            <ResourcePanel resource="artists" queryKey="/artists" label="un artiste" items={artists.items} list={artists} detailPath="/artists" Form={ArtistForm}
              renderRow={(a) => <RowTitle title={a.name} sub={(a.tags || []).join(" · ") || "—"} />} />
          </TabsContent>
          <TabsContent value="places">
            <HoursSync />
            <PlaceReview />
            <ResourcePanel resource="places" queryKey="/places" label="un lieu" items={places.data} Form={PlaceForm}
              renderRow={(p) => <RowTitle title={p.name} sub={`${p.city} · ${p.country}`} tag={(p.categories?.length ? p.categories : [p.category]).map((c) => PLACE_LABEL[c]).join(" · ")} />} />
          </TabsContent>
          <TabsContent value="messages"><Messages /></TabsContent>
          <TabsContent value="newsletter"><Newsletter /></TabsContent>
          <TabsContent value="donations"><Donations /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
